/**
 * Cloudflare Turnstile Component
 * 
 * This component integrates Cloudflare Turnstile (CAPTCHA) into the application.
 * It provides a visual challenge widget that users must complete before accessing
 * authentication features.
 * 
 * @module Turnstile
 */

import React, { useEffect, useRef, useCallback, useImperativeHandle, forwardRef } from 'react'

// Extend the Window interface to include Turnstile
declare global {
    interface Window {
        turnstile?: {
            render: (element: string | HTMLElement, options: TurnstileOptions) => string
            reset: (widgetId: string) => void
            remove: (widgetId: string) => void
            getResponse: (widgetId: string) => string
        }
    }
}

interface TurnstileOptions {
    sitekey: string
    callback?: (token: string) => void
    'error-callback'?: () => void
    'expired-callback'?: () => void
    'timeout-callback'?: () => void
    theme?: 'light' | 'dark' | 'auto'
    size?: 'normal' | 'compact'
    action?: string
    cData?: string
    'refresh-expired'?: 'auto' | 'manual' | 'never'
}

interface TurnstileProps {
    siteKey: string
    onVerify: (token: string) => void
    onError?: () => void
    onExpire?: () => void
    onTimeout?: () => void
    theme?: 'light' | 'dark' | 'auto'
    size?: 'normal' | 'compact'
    action?: string
    className?: string
}

export interface TurnstileRef {
    reset: () => void
}

/**
 * Turnstile Component
 * 
 * Renders Cloudflare Turnstile widget and handles verification callbacks
 * 
 * @param {TurnstileProps} props - Component properties
 * @param {React.Ref} ref - Ref for accessing reset method
 * @returns {JSX.Element} Turnstile widget container
 */
const Turnstile = forwardRef<TurnstileRef, TurnstileProps>(({
    siteKey,
    onVerify,
    onError,
    onExpire,
    onTimeout,
    theme = 'auto',
    size = 'normal',
    action,
    className = '',
}, ref) => {
    const containerRef = useRef<HTMLDivElement>(null)
    const widgetIdRef = useRef<string | null>(null)
    
    // Store callbacks in refs to avoid re-rendering widget on callback changes
    const onVerifyRef = useRef(onVerify)
    const onErrorRef = useRef(onError)
    const onExpireRef = useRef(onExpire)
    const onTimeoutRef = useRef(onTimeout)
    
    // Update refs when callbacks change
    useEffect(() => {
        onVerifyRef.current = onVerify
        onErrorRef.current = onError
        onExpireRef.current = onExpire
        onTimeoutRef.current = onTimeout
    }, [onVerify, onError, onExpire, onTimeout])

    /**
     * Reset the Turnstile widget to generate a new token
     */
    const reset = useCallback(() => {
        if (window.turnstile && widgetIdRef.current) {
            try {
                window.turnstile.reset(widgetIdRef.current)
            } catch (error) {
                console.error('Error resetting Turnstile widget:', error)
            }
        }
    }, [])

    // Expose reset method to parent component
    useImperativeHandle(ref, () => ({
        reset
    }), [reset])

    /**
     * Render Turnstile widget
     */
    useEffect(() => {
        // Wait for Turnstile script to load
        const renderWidget = () => {
            if (window.turnstile && containerRef.current && !widgetIdRef.current) {
                try {
                    const options: TurnstileOptions = {
                        sitekey: siteKey,
                        callback: (token: string) => {
                            if (token && onVerifyRef.current) {
                                onVerifyRef.current(token)
                            }
                        },
                        'error-callback': () => {
                            if (onErrorRef.current) {
                                onErrorRef.current()
                            }
                        },
                        'expired-callback': () => {
                            if (onExpireRef.current) {
                                onExpireRef.current()
                            }
                        },
                        'timeout-callback': () => {
                            if (onTimeoutRef.current) {
                                onTimeoutRef.current()
                            }
                        },
                        theme,
                        size,
                        'refresh-expired': 'auto',
                    }

                    // Add action if provided
                    if (action) {
                        options.action = action
                    }

                    widgetIdRef.current = window.turnstile.render(containerRef.current, options)
                } catch (error) {
                    console.error('Error rendering Turnstile widget:', error)
                }
            }
        }

        // Check if Turnstile is already loaded
        if (window.turnstile) {
            renderWidget()
        } else {
            // Wait for Turnstile to load
            const checkInterval = setInterval(() => {
                if (window.turnstile) {
                    clearInterval(checkInterval)
                    renderWidget()
                }
            }, 100)

            // Cleanup interval after 10 seconds
            setTimeout(() => clearInterval(checkInterval), 10000)

            return () => clearInterval(checkInterval)
        }

        // Cleanup on unmount
        return () => {
            if (window.turnstile && widgetIdRef.current) {
                try {
                    window.turnstile.remove(widgetIdRef.current)
                } catch (error) {
                    console.error('Error removing Turnstile widget:', error)
                }
                widgetIdRef.current = null
            }
        }
        // Only re-run if siteKey, theme, size, or action changes
        // Callback changes won't trigger re-render because we use refs
    }, [siteKey, theme, size, action])

    return <div ref={containerRef} className={className} />
})

Turnstile.displayName = 'Turnstile'

export default React.memo(Turnstile)

